// =====================================================
// Swiss Finance OS - Datenschutzerklärung
// DSGVO/FADP-konforme Datenschutzerklärung für Buchhaltungssoftware
// =====================================================

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="mb-header-mb">
          <Link to="/" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zur Anwendung
          </Link>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Datenschutzerklärung
          </h1>
          <p className="text-gray-600">
            Stand: {new Date().toLocaleDateString('de-CH')} | 
            Letzte Aktualisierung: {new Date().toLocaleDateString('de-CH')}
          </p>
        </div>

        {/* Kontaktinformationen */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Verantwortlicher
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p><strong>Togeco Buchhaltung</strong></p>
              <p className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                [Adresse einfügen]
              </p>
              <p className="flex items-center">
                <Phone className="h-4 w-4 mr-2" />
                [Telefon einfügen]
              </p>
              <p className="flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                [E-Mail einfügen]
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Hauptinhalt */}
        <div className="space-y-8">
          
          {/* 1. Allgemeine Informationen */}
          <Card>
            <CardHeader>
              <CardTitle>1. Allgemeine Informationen zum Datenschutz</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Der Schutz Ihrer persönlichen Daten ist uns ein besonderes Anliegen. 
                Diese Datenschutzerklärung informiert Sie über die Art, den Umfang und 
                Zweck der Verarbeitung von personenbezogenen Daten durch Togeco Buchhaltung.
              </p>
              <p>
                Wir verarbeiten personenbezogene Daten im Einklang mit der 
                Datenschutz-Grundverordnung (DSGVO) und dem Schweizer Bundesgesetz 
                über den Datenschutz (FADP).
              </p>
            </CardContent>
          </Card>

          {/* 2. Verarbeitete Datenkategorien */}
          <Card>
            <CardHeader>
              <CardTitle>2. Verarbeitete Datenkategorien</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Kundendaten:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Name, Vorname, Firmenname</li>
                  <li>Anschrift (Straße, PLZ, Ort)</li>
                  <li>Kontaktdaten (E-Mail, Telefon)</li>
                  <li>Geburtsdatum (bei natürlichen Personen)</li>
                  <li>Handelsregisternummer (bei Unternehmen)</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Buchhaltungsdaten:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Bankverbindungen (IBAN, BIC)</li>
                  <li>Transaktionsdaten</li>
                  <li>Rechnungsdaten</li>
                  <li>Steuerrelevante Informationen</li>
                  <li>Buchungsbelege und Dokumente</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Technische Daten:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>IP-Adressen</li>
                  <li>Browser-Informationen</li>
                  <li>Nutzungsdaten der Anwendung</li>
                  <li>Cookies und ähnliche Technologien</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 3. Verarbeitungszwecke und Rechtsgrundlagen */}
          <Card>
            <CardHeader>
              <CardTitle>3. Verarbeitungszwecke und Rechtsgrundlagen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-3 text-left">Zweck</th>
                      <th className="border border-gray-300 p-3 text-left">Rechtsgrundlage</th>
                      <th className="border border-gray-300 p-3 text-left">Datenkategorien</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 p-3">Buchhaltungsdienstleistungen</td>
                      <td className="border border-gray-300 p-3">Art. 6(1)(b) DSGVO (Vertragserfüllung)</td>
                      <td className="border border-gray-300 p-3">Kundendaten, Buchhaltungsdaten</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Steuerberatung</td>
                      <td className="border border-gray-300 p-3">Art. 6(1)(b) DSGVO (Vertragserfüllung)</td>
                      <td className="border border-gray-300 p-3">Buchhaltungsdaten, Steuerdaten</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Rechtliche Aufbewahrungspflichten</td>
                      <td className="border border-gray-300 p-3">Art. 6(1)(c) DSGVO (Rechtliche Verpflichtung)</td>
                      <td className="border border-gray-300 p-3">Alle Datenkategorien</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Website-Betrieb</td>
                      <td className="border border-gray-300 p-3">Art. 6(1)(f) DSGVO (Berechtigtes Interesse)</td>
                      <td className="border border-gray-300 p-3">Technische Daten</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 p-3">Marketing (mit Einwilligung)</td>
                      <td className="border border-gray-300 p-3">Art. 6(1)(a) DSGVO (Einwilligung)</td>
                      <td className="border border-gray-300 p-3">Kontaktdaten</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* 4. Datenweitergabe */}
          <Card>
            <CardHeader>
              <CardTitle>4. Datenweitergabe an Dritte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Wir geben Ihre personenbezogenen Daten nur an Dritte weiter, wenn:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Sie ausdrücklich eingewilligt haben</li>
                <li>die Weitergabe zur Erfüllung des Vertrags erforderlich ist</li>
                <li>wir rechtlich dazu verpflichtet sind</li>
                <li>wir ein berechtigtes Interesse haben</li>
              </ul>
              
              <div className="mt-4">
                <h4 className="font-semibold mb-2">Auftragsverarbeiter:</h4>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>Supabase:</strong> Datenbankhosting (EU)</li>
                  <li><strong>OpenAI/Anthropic:</strong> KI-Verarbeitung (mit Anonymisierung)</li>
                  <li><strong>Hosting-Provider:</strong> Serverhosting</li>
                  <li><strong>E-Mail-Provider:</strong> Kommunikation</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 5. Speicherdauer */}
          <Card>
            <CardHeader>
              <CardTitle>5. Speicherdauer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold">Buchhaltungsdaten:</h4>
                  <p className="text-sm">10 Jahre nach Ende des Geschäftsjahres (Art. 958f OR)</p>
                </div>
                <div>
                  <h4 className="font-semibold">Steuerrelevante Daten:</h4>
                  <p className="text-sm">10 Jahre nach Ende des Steuerjahres</p>
                </div>
                <div>
                  <h4 className="font-semibold">Kundendaten:</h4>
                  <p className="text-sm">Bis zur Beendigung des Mandatsverhältnisses + 10 Jahre</p>
                </div>
                <div>
                  <h4 className="font-semibold">Technische Daten:</h4>
                  <p className="text-sm">Maximal 2 Jahre</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 6. Ihre Rechte */}
          <Card>
            <CardHeader>
              <CardTitle>6. Ihre Rechte als betroffene Person</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Auskunftsrecht (Art. 15 DSGVO)</h4>
                  <p className="text-sm">Sie haben das Recht, Auskunft über die zu Ihrer Person gespeicherten Daten zu erhalten.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Berichtigungsrecht (Art. 16 DSGVO)</h4>
                  <p className="text-sm">Sie können die Berichtigung unrichtiger Daten verlangen.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Löschungsrecht (Art. 17 DSGVO)</h4>
                  <p className="text-sm">Sie können die Löschung Ihrer Daten verlangen (unter bestimmten Voraussetzungen).</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Einschränkungsrecht (Art. 18 DSGVO)</h4>
                  <p className="text-sm">Sie können die Einschränkung der Verarbeitung verlangen.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Datenübertragbarkeit (Art. 20 DSGVO)</h4>
                  <p className="text-sm">Sie können die Übertragung Ihrer Daten in einem strukturierten Format verlangen.</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Widerspruchsrecht (Art. 21 DSGVO)</h4>
                  <p className="text-sm">Sie können der Verarbeitung aus Gründen Ihres besonderen Interesses widersprechen.</p>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-2">Ausübung Ihrer Rechte:</h4>
                <p className="text-sm mb-3">
                  Um Ihre Rechte auszuüben, wenden Sie sich bitte an:
                </p>
                <div className="space-y-1 text-sm">
                  <p>E-Mail: [datenschutz@togeco.ch]</p>
                  <p>Post: Togeco Buchhaltung, [Adresse]</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 7. Cookies und Tracking */}
          <Card>
            <CardHeader>
              <CardTitle>7. Cookies und Tracking-Technologien</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Wir verwenden Cookies und ähnliche Technologien, um unsere Website zu betreiben 
                und Ihr Nutzererlebnis zu verbessern.
              </p>
              
              <div>
                <h4 className="font-semibold mb-2">Cookie-Kategorien:</h4>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded">
                    <h5 className="font-medium">Essenzielle Cookies</h5>
                    <p className="text-sm text-gray-600">
                      Notwendig für den Betrieb der Website. Rechtsgrundlage: Art. 6(1)(f) DSGVO.
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <h5 className="font-medium">Analytics Cookies</h5>
                    <p className="text-sm text-gray-600">
                      Helfen uns, die Nutzung zu verstehen. Rechtsgrundlage: Art. 6(1)(a) DSGVO (Einwilligung).
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <h5 className="font-medium">Marketing Cookies</h5>
                    <p className="text-sm text-gray-600">
                      Für personalisierte Werbung. Rechtsgrundlage: Art. 6(1)(a) DSGVO (Einwilligung).
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 8. Datensicherheit */}
          <Card>
            <CardHeader>
              <CardTitle>8. Datensicherheit</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Wir treffen angemessene technische und organisatorische Maßnahmen zum Schutz 
                Ihrer personenbezogenen Daten:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>SSL/TLS-Verschlüsselung für alle Datenübertragungen</li>
                <li>Verschlüsselung sensibler Daten in der Datenbank</li>
                <li>Regelmäßige Sicherheitsupdates</li>
                <li>Zugriffskontrollen und Benutzerauthentifizierung</li>
                <li>Regelmäßige Backups</li>
                <li>Schulung der Mitarbeiter im Datenschutz</li>
              </ul>
            </CardContent>
          </Card>

          {/* 9. Beschwerderecht */}
          <Card>
            <CardHeader>
              <CardTitle>9. Beschwerderecht bei der Aufsichtsbehörde</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p>
                Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über 
                die Verarbeitung Ihrer personenbezogenen Daten durch uns zu beschweren.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Schweiz:</h4>
                  <p className="text-sm">
                    Eidgenössischer Datenschutz- und Öffentlichkeitsbeauftragter (EDÖB)<br/>
                    Feldeggweg 1, 3003 Bern<br/>
                    Tel: +41 58 462 43 95<br/>
                    E-Mail: info@edoeb.admin.ch
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">EU (falls anwendbar):</h4>
                  <p className="text-sm">
                    Datenschutzbehörde des Landes Ihres Wohnsitzes oder 
                    des Landes, in dem der Verstoß aufgetreten ist.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 10. Änderungen */}
          <Card>
            <CardHeader>
              <CardTitle>10. Änderungen dieser Datenschutzerklärung</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                Wir behalten uns vor, diese Datenschutzerklärung zu aktualisieren, 
                um sie an geänderte Rechtslagen oder bei Änderungen unserer 
                Dienstleistungen sowie der Datenverarbeitung anzupassen.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              © 2024 Togeco Buchhaltung. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-4">
              <Link to="/impressum" className="text-sm text-blue-600 hover:underline">
                Impressum
              </Link>
              <Link to="/terms" className="text-sm text-blue-600 hover:underline">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
